import * as React from 'react'

import { tv, type VariantProps } from 'tailwind-variants'

import type { Tailwind as TailwindTypes } from '@/tailwind-types'
import { cn } from '@/utils'

export const textVariants = tv({
  base: 'leading-default',
  variants: {
    italic: {
      true: 'italic',
      false: ''
    },
    level: {
      caption: '',
      div: '',
      h1: '',
      h2: '',
      h3: '',
      h4: '',
      p: '',
      small: '',
      span: '',
      strong: ''
    },
    size: {
      'heading-h1':
        'font-heading text-heading-h1 xs:text-heading-h1-xs sm:text-heading-h1-sm md:text-heading-h1-md lg:text-heading-h1-lg xl:text-heading-h1-xl 2xl:text-heading-h1-2xl 3xl:text-heading-h1-3xl 4xl:text-heading-h1-4xl',
      'heading-h2':
        'font-heading text-heading-h2 xs:text-heading-h2-xs sm:text-heading-h2-sm md:text-heading-h2-md lg:text-heading-h2-lg xl:text-heading-h2-xl 2xl:text-heading-h2-2xl 3xl:text-heading-h2-3xl 4xl:text-heading-h2-4xl',
      'heading-h3':
        'font-heading text-heading-h3 xs:text-heading-h3-xs sm:text-heading-h3-sm md:text-heading-h3-md lg:text-heading-h3-lg xl:text-heading-h3-xl 2xl:text-heading-h3-2xl 3xl:text-heading-h3-3xl 4xl:text-heading-h3-4xl',
      'heading-h4':
        'font-heading text-heading-h4 xs:text-heading-h4-xs sm:text-heading-h4-sm md:text-heading-h4-md lg:text-heading-h4-lg xl:text-heading-h4-xl 2xl:text-heading-h4-2xl 3xl:text-heading-h4-3xl 4xl:text-heading-h4-4xl',
      'title-xl':
        'font-heading text-title-xl xs:text-title-xl-xs sm:text-title-xl-sm md:text-title-xl-md lg:text-title-xl-lg xl:text-title-xl-xl 2xl:text-title-xl-2xl 3xl:text-title-xl-3xl 4xl:text-title-xl-4xl',
      'title-lg':
        'font-heading text-title-lg xs:text-title-lg-xs sm:text-title-lg-sm md:text-title-lg-md lg:text-title-lg-lg xl:text-title-lg-xl 2xl:text-title-lg-2xl 3xl:text-title-lg-3xl 4xl:text-title-lg-4xl',
      'body-lg':
        'font-body text-body-lg xs:text-body-lg-xs sm:text-body-lg-sm md:text-body-lg-md lg:text-body-lg-lg xl:text-body-lg-xl 2xl:text-body-lg-2xl 3xl:text-body-lg-3xl 4xl:text-body-lg-4xl',
      'body-md':
        'font-body text-body-md xs:text-body-md-xs sm:text-body-md-sm md:text-body-md-md lg:text-body-md-lg xl:text-body-md-xl 2xl:text-body-md-2xl 3xl:text-body-md-3xl 4xl:text-body-md-4xl',
      'body-xl':
        'font-body text-body-xl xs:text-body-xl-xs sm:text-body-xl-sm md:text-body-xl-md lg:text-body-xl-lg xl:text-body-xl-xl 2xl:text-body-xl-2xl 3xl:text-body-xl-3xl 4xl:text-body-xl-4xl',
      'caption-md':
        'font-body text-caption-md xs:text-caption-md-xs sm:text-caption-md-sm md:text-caption-md-md lg:text-caption-md-lg xl:text-caption-md-xl 2xl:text-caption-md-2xl 3xl:text-caption-md-3xl 4xl:text-caption-md-4xl',
      'caption-sm':
        'font-body text-caption-sm xs:text-caption-sm-xs sm:text-caption-sm-sm md:text-caption-sm-md lg:text-caption-sm-lg xl:text-caption-sm-xl 2xl:text-caption-sm-2xl 3xl:text-caption-sm-3xl 4xl:text-caption-sm-4xl',
      inherit: ''
    },
    weight: {
      bold: 'font-bold',
      extraBold: 'font-extrabold',
      medium: 'font-medium',
      regular: 'font-normal',
      semibold: 'font-semibold',
      inherit: ''
    }
  },
  compoundVariants: [
    {
      level: 'strong',
      weight: 'bold'
    }
  ],
  defaultVariants: {
    level: 'p',
    size: 'inherit',
    /* The default weight is regular (400), don't confuse with medium (500).
     * The weight names can be confusing but the weights are different.
     * You can check more in: https://www.figma.com/design/ucYJUbUhCYwYeXaxDxroAg/Acqua---Alternative-Payments-Design-Library?node-id=8337-9125&t=n8mSo8ymsdBloEl9-4
     */
    weight: 'regular'
  }
})

type TextElement =
  | 'p'
  | 'span'
  | 'div'
  | 'small'
  | 'strong'
  | 'caption'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'

type TextOwnProps = VariantProps<typeof textVariants> & {
  align?: TailwindTypes['textAlign']
  color?: TailwindTypes['color']
  italic?: boolean
  margin?: TailwindTypes['margin']
  padding?: TailwindTypes['padding']
  textTransform?: TailwindTypes['textTransform']
  breakWords?: boolean
  truncate?: boolean
  children?: React.ReactNode
}

export type TTextProps<C extends TextElement = 'p'> = TextOwnProps & {
  level?: C
} & Omit<React.ComponentPropsWithoutRef<C>, keyof TextOwnProps | 'level'>

export const Text = <C extends TextElement = 'p'>({
  align,
  children,
  color,
  italic,
  level,
  margin,
  padding,
  size,
  textTransform,
  weight,
  breakWords,
  truncate,
  className,
  ...restProps
}: TTextProps<C>): JSX.Element => {
  const Tag = (level || textVariants.defaultVariants.level) as React.ElementType

  return (
    <Tag
      className={cn(
        textVariants({
          italic,
          level,
          size,
          weight
        }),
        align,
        color,
        margin,
        padding,
        textTransform,
        breakWords && 'break-all',
        truncate && 'block truncate',
        className
      )}
      {...(restProps as React.ComponentPropsWithoutRef<typeof Tag>)}
    >
      {children}
    </Tag>
  )
}
